import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch'
import {
  Box,
  Button,
  type ButtonProps,
  Flex,
  Menu,
  Text,
  TextInput,
} from '@mantine/core'
import { clsx } from 'clsx'
import { useState, useEffect } from 'react'

import { useTranslation } from '@flecto/translations'

import { Icon } from '../../src'
import styles from './styles.module.css'
import { type CountryPhonePrefix, useCountriesList } from './useCountriesList'

type DropdownCountryProps = {
  size?: ButtonProps['size']
  valueDialCode?: string
  defaultValueDialCode?: string
  onChangeDialCode?: (dialCode: string) => void
}

type MenuItemProps = {
  country: CountryPhonePrefix
  onChangeCountry: (country: CountryPhonePrefix) => void
}

export function DropdownCountries({
  size = 'md',
  valueDialCode,
  defaultValueDialCode,
  onChangeDialCode,
}: DropdownCountryProps) {
  const hintCountries = ['PT']
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<CountryPhonePrefix>()
  const { t } = useTranslation('shared', { keyPrefix: 'forms' })
  const { countries, defaultCountry } = useCountriesList()

  const handleChangeCountry = (country: CountryPhonePrefix) => {
    onChangeDialCode?.(country.dialCode)
    setSelected(country)
  }

  const countriesHint = countries.filter((country) =>
    hintCountries.includes(country.countryCode)
  )

  const countriesRest = countries
    .filter((country) => !hintCountries.includes(country.countryCode))
    .filter((country) => {
      if (search) {
        return (
          country.countryName
            .toLocaleLowerCase()
            .indexOf(search.toLocaleLowerCase()) !== -1
        )
      }
      return true
    })

  useEffect(() => {
    if (!valueDialCode) {
      return
    }

    const found = countries.find(
      (country) => country.dialCode === valueDialCode
    )

    if (found) {
      setSelected(found)
    }
  }, [valueDialCode])

  useEffect(() => {
    if (!defaultValueDialCode) {
      return
    }
    const found = countries.find(
      (country) => country.dialCode === defaultValueDialCode
    )

    if (found) {
      setSelected(found)
      onChangeDialCode?.(found.dialCode)
    }
  }, [defaultValueDialCode])

  useEffect(() => {
    if (!defaultValueDialCode && !valueDialCode && defaultCountry) {
      setSelected(defaultCountry)
      onChangeDialCode?.(defaultCountry.dialCode)
    }
  }, [])

  return (
    <Menu position='bottom-start' withinPortal zIndex={9999999}>
      <Menu.Target>
        <Button
          variant='default'
          data-testid='dropdown-target'
          className={clsx(styles.dropdownTrigger, styles[size])}
        >
          <Flex gap='sm' align='center' justify='flex-start'>
            <Text size='lg'>{selected?.countryFlag ?? '🌍'}</Text>
            <Text color='dimmed'>{selected?.dialCode ?? '+00'}</Text>
          </Flex>
        </Button>
      </Menu.Target>
      <Menu.Dropdown data-testid='dropdown-content'>
        <TextInput
          value={search}
          variant='unstyled'
          onChange={(e) => setSearch(e.target.value)}
          leftSection={<Icon icon={faSearch} />}
          className={styles.inputSearch}
          placeholder={t('placeholders.search_country')}
          data-testid='search-input'
        />
        <Box className={styles.dropdownWrapper}>
          {countriesHint.map((country) => (
            <MenuItem
              key={country.countryCode}
              country={country}
              onChangeCountry={(c) => handleChangeCountry(c)}
              data-testid='hint-country'
            />
          ))}
          <Menu.Divider />
          {countriesRest.map((country) => (
            <MenuItem
              key={country.countryCode}
              country={country}
              onChangeCountry={(c) => handleChangeCountry(c)}
              data-testid='rest-country'
            />
          ))}
        </Box>
      </Menu.Dropdown>
    </Menu>
  )
}

function MenuItem({ country, onChangeCountry, ...props }: MenuItemProps) {
  return (
    <Menu.Item {...props} onClick={() => onChangeCountry(country)}>
      <Flex align='baseline' gap='sm'>
        <Text>{country.countryFlag}</Text>
        <Text>{country.countryName}</Text>
        <Text c='dimmed' size='sm'>
          {country.dialCode}
        </Text>
      </Flex>
    </Menu.Item>
  )
}
